"use client";
import { useState } from "react";
import Image from "next/image";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { IndustryAccordionProps } from "@/lib/Interface";

export default function IndustryAccordion({
  items,
  imageUrl,
}: IndustryAccordionProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const handleToggle = (index: number) => {
    setActiveIndex(index);
  };

  const formatIndex = (index: number): string => {
    return `0${index + 1}`;
  };
  const isMobile = useIsMobile();

  return (
    <div className="w-full py-[0px] pb-[30px] lg:pb-[60px] lg:py-[60px] bg-white">
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)]">
        <div className="grid grid-cols-1 relative xl:grid-cols-[40%_60%] gap-[40px] lg:gap-[80px] items-start">
          {/* Left side - Image */}
          <div className="hidden lg:block xl:sticky xl:top-[50px] w-full">
            <div className="relative overflow-hidden rounded-lg">
              {!isMobile && (
                <Image
                  src={
                    items[activeIndex]?.images
                      ? items[activeIndex]?.images
                      : imageUrl
                  }
                  alt="Industry illustration"
                  className="w-full h-[500px] lg:h-[800px] object-cover rounded-lg shadow-lg relative z-10"
                  width={800} // Aspect ratio hint for Next.js
                  height={1000}
                />
              )}

              {/* Optional: Add a subtle overlay if the image feels too narrow */}
              <div className="absolute inset-0 bg-black/5 z-15 pointer-events-none"></div>
            </div>
          </div>

          {/* Right side - Accordion */}
          <div className="flex flex-col w-full pl-[35px]">
            {items?.map((item, index) => {
              const isActive = index === activeIndex;

              return (
                <div
                  key={index}
                  className="relative mb-[15px] lg:mb-[20px] last:mb-0"
                >
                  {/* Progress Indicator */}
                  <div className="absolute h-full flex items-center flex-col -left-[40px] top-[0px] lg:top-[25px]">
                    <div
                      className={`clash-font text-[20px] transition-colors duration-300 ${
                        isActive
                          ? "text-[#225BD4] opacity-100"
                          : "text-[#5C5C5E] opacity-50"
                      }`}
                    >
                      {formatIndex(index)}
                    </div>

                    <div className="w-[1px] flex-1 bg-[#D9E2ED] my-2 relative overflow-hidden">
                      <div
                        className="absolute top-0 w-full bg-[#225BD4] transition-transform duration-500 origin-top"
                        style={{
                          height: "100%",
                          transform: `scaleY(${isActive ? 1 : 0})`, // ScaleY is better for performance than height
                        }}
                      />
                    </div>
                  </div>

                  {/* Accordion Content */}
                  <div className="w-full">
                    <button
                      className="flex justify-between items-center lg:py-[15px] cursor-pointer w-full text-left focus:outline-none"
                      onClick={() => handleToggle(index)}
                      aria-expanded={isActive}
                    >
                      <h3
                        className={`text-[length:var(--section-subtitle)] clash-font transition-all duration-300 ${
                          isActive
                            ? "text-[#225BD4] font-medium"
                            : "text-[#5C5C5E] opacity-50"
                        }`}
                      >
                        {item.title}
                      </h3>
                    </button>

                    {/* Smooth Grid-based expansion */}
                    <div
                      className={`grid transition-all duration-500 ease-in-out ${
                        isActive
                          ? "grid-rows-[1fr] opacity-100 mb-4"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p
                          className="text-[length:var(--mega-menu-sub)] leading-[1.8] text-[#636364]"
                          dangerouslySetInnerHTML={{
                            __html: item.content ?? "",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
